/**
 * Columns for the PO-line drill-downs (PO Lines, OTIF, Arrears, Qty Diff —
 * one format, review items 11-14). Customer review 11 Sep.
 *
 * "Last Received Date" is the latest GRN against the line (a line can take
 * several receipts), computed for the page in one grouped Max query
 * (`last_received_date`, see POLineListingMixin._enrich_page_aggregates) —
 * never per row. It replaces the old "Delivery Date" column (msg 11b).
 */
import { Badge } from '../../../components/ui/badge';
import PoHeaderLink from '../../../components/common/PoHeaderLink';
import PoLineLink from '../../../components/common/PoLineLink';
import { PurchaseOrderLine } from '../../../lib/api';
import { DrilldownColumn, DrilldownFormatters, dateOf, num, yesNo } from './columns';
import { ColumnOptions } from './columnsFor';

function otifVerdict(v: boolean | null | undefined): string | null {
  if (v === true) return 'Pass';
  if (v === false) return 'Fail';
  return null;
}

/** Ordered − received: the line's term of the (net) Qty Diff cell. */
function qtyDiff(l: PurchaseOrderLine): number | null {
  const ordered = num(l.quantity);
  const received = num(l.received_qty || '0');
  return ordered === null || received === null ? null : Math.round((ordered - received) * 1000) / 1000;
}

export function poLineColumns(f: DrilldownFormatters, options: ColumnOptions): DrilldownColumn<PurchaseOrderLine>[] {
  const customerView = !options.isSupplier;
  const diff: DrilldownColumn<PurchaseOrderLine>[] = options.metric === 'qty_diff' ? [{
    key: 'qty_diff', label: 'Qty Diff', kind: 'qty', align: 'right', className: 'font-medium',
    title: 'Qty Ordered − Qty Received. The column sums to the Qty Diff cell (a net figure: over- and under-deliveries offset)',
    value: (l) => qtyDiff(l),
  }] : [];

  return [
    // CR-1154: PO number links to the PO header; line to the line item.
    {
      key: 'po_number', label: 'PO Number', className: 'font-medium',
      value: (l) => l.po_number ?? null,
      render: (l) => <PoHeaderLink poNumber={l.po_number}>{l.po_number || '-'}</PoHeaderLink>,
    },
    {
      key: 'line_number', label: 'Line #',
      value: (l) => l.line_number,
      render: (l) => <PoLineLink poLineId={l.id}>{l.line_number}</PoLineLink>,
    },
    // The PO's own customer site (review item 3: PRO0005371 is BENGALURU).
    { key: 'customer_name', label: 'Customer', value: (l) => l.customer_name ?? null },
    { key: 'supplier_group_name', label: 'Supplier Group', value: (l) => l.supplier_group_name ?? null },
    { key: 'supplier_name', label: 'Supplier Site', value: (l) => l.supplier_name ?? null },
    { key: 'part_number', label: 'Part Number', value: (l) => l.part_number },
    { key: 'description', label: 'Description', truncate: true, value: (l) => l.description },
    { key: 'quantity', label: 'Qty Ordered', kind: 'qty', align: 'right', value: (l) => l.quantity },
    { key: 'received_qty', label: 'Qty Received', kind: 'qty', align: 'right', className: 'text-green-600', value: (l) => l.received_qty || '0' },
    ...diff,
    { key: 'outstanding_qty', label: 'Outstanding', kind: 'qty', align: 'right', className: 'text-orange-600', value: (l) => l.outstanding_qty || '0' },
    { key: 'unit_price', label: 'Unit Price', kind: 'money', align: 'right', value: (l) => l.unit_price, currency: (l) => l.currency },
    ...(customerView ? [{
      key: 'converted_unit_price', label: 'Converted Unit Price', kind: 'money', align: 'right',
      title: "Unit price in the customer's reporting currency",
      value: (l: PurchaseOrderLine) => l.converted_unit_price ?? null,
      currency: (l: PurchaseOrderLine) => l.converted_currency,
    } as DrilldownColumn<PurchaseOrderLine>] : []),
    { key: 'currency', label: 'Currency', value: (l) => l.currency ?? null },
    { key: 'mrp_scheduled_date', label: 'MRP Date', value: (l) => dateOf(f.formatDate, l.mrp_scheduled_date) },
    {
      key: 'supplier_promise_date', label: 'Review Date (LSP)',
      title: 'Latest supplier promise date',
      value: (l) => dateOf(f.formatDate, l.supplier_promise_date),
    },
    // ODD = the server's odd_date — the value the PO Lines grid's "Agreed
    // Date (ODD)" and the line pop-up show, and the date OTIF measures against.
    { key: 'odd', label: 'ODD', value: (l) => dateOf(f.formatDate, l.odd_date ?? l.delivery_date) },
    {
      key: 'last_received_date', label: 'Last Received Date',
      title: 'Date of the latest GRN received against this line',
      value: (l) => dateOf(f.formatDate, l.last_received_date),
    },
    { key: 'completed', label: 'Completed', align: 'center', value: (l) => yesNo(l.status === 'received') },
    {
      key: 'status', label: 'Status', align: 'center',
      value: (l) => l.status,
      render: (l) => <Badge variant="outline">{l.status}</Badge>,
    },
    {
      key: 'is_otif', label: 'OTIF', align: 'center',
      title: "The line's OTIF verdict (blank until the line is closed and judged)",
      value: (l) => otifVerdict(l.is_otif),
      render: (l) => {
        const verdict = otifVerdict(l.is_otif);
        if (!verdict) return <span className="text-muted-foreground">-</span>;
        return (
          <Badge className={verdict === 'Pass' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}>
            {verdict}
          </Badge>
        );
      },
    },
    { key: 'otif_acknowledged', label: 'OTIF Acknowledged', align: 'center', value: (l) => yesNo(l.otif_acknowledged) },
    // CR-6190: the manual OTIF override flag. Customer-only — the PO Lines
    // grid withholds it from suppliers (SUPPLIER_RESTRICTED_COLUMNS).
    ...(customerView ? [{
      key: 'otif_overridden', label: 'Overridden', align: 'center',
      value: (l: PurchaseOrderLine) => yesNo(l.otif_overridden),
      render: (l: PurchaseOrderLine) => l.otif_overridden
        ? <span className="px-2 py-1 rounded-full text-xs font-medium bg-amber-100 text-amber-800">Overridden</span>
        : <span className="text-muted-foreground">-</span>,
    } as DrilldownColumn<PurchaseOrderLine>] : []),
    { key: 'updated_at', label: 'Update Time', value: (l) => dateOf(f.formatDateTime, l.updated_at) },
  ];
}
